import React, {useEffect, useLayoutEffect, useMemo, useState} from 'react';
import MainLayout from '../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  RefreshControl,
  View,
  ScrollView,
} from 'react-native';
import {Theme} from '../../../theme/lightTheme';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import Icon from '../../../components/icon';
import {
  getByScreenSize,
  hdp,
  isWeb,
  wdp as staticWdp,
  wdp,
} from '../../../utils/responsive';
import screens from '../../../config/screens';
import Animated, {
  FadeInLeft,
  FadeInRight,
  FadeInUp,
  FadeOutUp,
  useAnimatedScrollHandler,
  useSharedValue,
} from 'react-native-reanimated';
import {DrugItemType, NewsItemType} from '../../../interfaces/api/tabs';
import {getNews, getPublishersByLetter} from '../tabNews/actions';
import {RootState} from '../../../redux/store';
import {connect, useSelector} from 'react-redux';
import EmptyList from '../../../components/emptyList';
import SearchListSkeleton from '../../../components/searchListSkeleton';
import Loader from '../../../components/loader';
import {NewsOptionTab, SpecialityType} from '../../../utils/enums';
import {showFiltersModal} from '../../../connected-components/filtersModal/actions';
import HeaderSearch from '../../../components/header/headerSearch';
import Config from 'react-native-config';
import {
  FilterItemContainer,
  FilterLabel,
  FiltersContainer,
  FilterValuesContainer,
} from '../drugs';
import DrugFilterItem from '../../../components/drugFilterItem';
import {getFilterItemByKey} from '../../../utils/helpers';
import {handleChange} from '../../../utils/form';
import {useCustomHeaderHeight} from '../../../utils/hooks/useHeaderHeight';
import {Space} from '../home';
import {TabNewsScreenProps} from '../../../interfaces/screens/tabNews';
import {FilterType} from '../../../interfaces/reducers/homeState';
import GenioLatestNews from '../../../components/genioLatestNews';
import SelectSummaries from '../../../components/selectSummaries';
import '../../../utils/webAnimations';

const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView);

const EmptyContainer = styled.View<{
  extraSpace?: number;
  responsiveWidth?: string;
}>`
  align-items: center;
  justify-content: center;
  align-self: center;
  width: ${({responsiveWidth}) => responsiveWidth};
  min-height: ${({extraSpace}) => hdp(75 - (extraSpace || 0) / 4)}px;
`;
const FiltersItemsContainer = styled(FiltersContainer)<{
  responsiveWidth: number;
}>`
  border-radius: 20px;
  width: ${({responsiveWidth}) => responsiveWidth}px;
  margin-vertical: ${({theme}) => theme.text.s55}px;
  align-self: center;
  background-color: transparent;
  z-index: 99;
`;
const IconContainer = styled(Animated.View)<{isPositionLeft?: boolean}>`
  position: absolute;
  top: ${wdp(1)}px;
  ${({isPositionLeft}) =>
    isPositionLeft ? `left:-${wdp(1)}px;` : `right:-${wdp(1)}px;`};
  z-index: 99;
  background-color: ${({theme}) => theme.primary + '22'};
  border-radius: 100px;
`;
const LatestNewsItem = styled(GenioLatestNews)`
  width: ${Platform.OS === 'web' ? '94%' : staticWdp(94) + 'px'};
  height: ${({theme}) => theme.text.s1 * 3}px;
  align-self: center;
  margin-bottom: ${({theme}) => theme.text.s12}px;
`;

/**
 * For You screen component.
 *
 * @param {ScreenProps} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const ForYou: React.FC<TabNewsScreenProps> = ({
  navigation,
  route,
}: TabNewsScreenProps) => {
  const theme = useTheme() as Theme;
  const {top: topInset, bottom: bottomInset} = useSafeAreaInsets();
  const headerHeight = useCustomHeaderHeight();
  const {
    filtersResult: prevFiltersResult,
    filters: prevFilters,
    tabId,
    search: prevSearch,
    isRecommendation,
    isRecommendationByTab,
    isHomeRecommendation,
    staticRecommendation,
    recommendationsFilters,
    newsFilters,
    summaryDateInfo,
  } = route?.params || {};
  const filters =
    (isRecommendationByTab ? recommendationsFilters : newsFilters) ||
    prevFilters;
  const publisherIdx = Array.isArray(filters)
    ? (filters || []).findIndex(i => i.key === 'publisher')
    : -1;

  const {currentUser} = useSelector((state: RootState) => state.auth);
  const pageSize =
    (currentUser?.email || '')?.trim().toLowerCase() === Config.APPLE_EMAIL
      ? 100
      : 10;
  const [filtersResult, setFiltersResult] = useState<any>(
    prevFiltersResult || {},
  );
  const [data, setData] = useState<NewsItemType[]>([]);
  const [localSearchResults, setLocalSearchResults] =
    useState<NewsItemType[]>(data);
  const [search, setSearch] = useState(prevSearch || '');
  const [newsCount, setNewsCount] = useState('0');
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);
  // Publishers filter
  const [isLoadingPublishers, setIsLoadingPublishers] = useState(false);
  const [isErrorPublishers, setIsErrorPublishers] = useState(false);
  const [publishersPage, setPublishersPage] = useState(1);
  const [isLastPagePublishers, setIsLastPagePublishers] =
    useState<boolean>(false);
  const [publishersData, setPublishersData] = useState<DrugItemType[]>([]);
  const [isPublisherVisible, setIsPublisherVisible] = useState(false);
  const [isFilterModalShouldClose, setIsFilterModalShouldClose] =
    useState<any>(false);
  const [selectedPublisher, setSelectedPublisher] = useState<any>({});
  const [selectedPublisherFilter, setSelectedPublisherFilter] = useState<any>(
    {},
  );
  const [selectedPublisherAlphabet, setSelectedPublisherAlphabet] =
    useState('');
  const [publisherHeight, setPublisherHeight] = useState(hdp(5));

  // Add refs and state for scroll handling
  const [_contentHeight, setContentHeight] = useState(0);
  const [_containerHeight, setContainerHeight] = useState(0);
  const [_currentScrollPosition, setCurrentScrollPosition] = useState(0);
  const loadMoreThreshold = 0.75; // Trigger when 75% scrolled

  const scrollY = useSharedValue(0);

  // Scroll handler for animations - used on mobile
  const animatedScrollHandler = useAnimatedScrollHandler(event => {
    scrollY.value = event.contentOffset.y;
  });

  // Web scroll handler that manages both animation and pagination
  const webScrollHandler = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    // Update animation value
    scrollY.value = event.nativeEvent.contentOffset.y;

    // Calculate if we need to load more content
    const {contentOffset, contentSize, layoutMeasurement} = event.nativeEvent;

    // Update tracking variables
    setCurrentScrollPosition(contentOffset.y);
    setContentHeight(contentSize.height);
    setContainerHeight(layoutMeasurement.height);

    // Calculate scroll percentage - how far down the user has scrolled
    const scrollPercentage =
      (contentOffset.y + layoutMeasurement.height) / contentSize.height;

    // If we've scrolled past the threshold, load more content
    if (scrollPercentage >= loadMoreThreshold && !isLoading && !isLastPage) {
      loadMore();
    }
  };

  const filtersNum = useMemo(
    () =>
      Object.keys(filtersResult || {}).filter(
        item => !!getFilterItemByKey(filters, item, filtersResult[item]),
      ).length + (filtersResult?.publisher ? 1 : 0),
    [filtersResult],
  );

  const loadMore = async (refresh = false) => {
    if (staticRecommendation) {
      const specialtyId = currentUser?.specialty_id;
      const specialtyMapping: Record<SpecialityType, any> = {
        [SpecialityType.Cardiology]:
          staticRecommendation[SpecialityType.Cardiology],
        [SpecialityType.Respiratory]:
          staticRecommendation[SpecialityType.Respiratory],
        [SpecialityType.Endocrinology]:
          staticRecommendation[SpecialityType.Endocrinology],
        [SpecialityType.Dermatology]:
          staticRecommendation[SpecialityType.Dermatology],
        [SpecialityType.Neurology]:
          staticRecommendation[SpecialityType.Neurology],
        [SpecialityType.Ophthalmology]:
          staticRecommendation[SpecialityType.Ophthalmology],
        [SpecialityType.Pediatrics]:
          staticRecommendation[SpecialityType.Pediatrics],
        [SpecialityType.Urology]: staticRecommendation[SpecialityType.Urology],
        [SpecialityType.Anti]: staticRecommendation[SpecialityType.Anti],
        [SpecialityType.Gastroenterology]:
          staticRecommendation[SpecialityType.Gastroenterology],
        [SpecialityType.rheumatology]:
          staticRecommendation[SpecialityType.rheumatology],
      };
      const specialtyData =
        specialtyMapping[+(specialtyId || '') as SpecialityType] || [];
      setData(specialtyData);
      return;
    }
    if (isLoading && !refresh) {
      return;
    }
    setIsError(false);
    if (!isLastPage || refresh) {
      if (refresh) {
        setData([]);
      }
      setIsLoading(true);
      const result = await getNews({
        keywords: search || '',
        tab_id: tabId || null,
        isRecommendations: isRecommendationByTab,
        page: refresh ? 1 : page,
        pageSize,
        ...filtersResult,
      });

      if (!result) {
        setIsError(true);
        setIsLastPage(true);
        setIsLoading(false);
        return;
      }

      setNewsCount(result?.total ? result?.total + '' : '0');
      setData(prev => {
        if (refresh) {
          return [...result?.data!];
        } else {
          return [...prev, ...result?.data!].filter((value, index, self) => {
            return (
              self.findIndex(obj =>
                //@ts-ignore
                ['id'].every(key => obj[key] === value[key]),
              ) === index
            );
          });
        }
      });
      setPage(prev => (refresh ? 2 : prev + 1));
      const totalPageCount = Math.ceil(
        parseFloat(result?.total ? result?.total + '' : '2') / pageSize,
      );
      setIsLastPage((refresh ? 1 : page) >= totalPageCount);
      setIsLoading(false);
    }
  };

  const loadMorePublishers = async (refresh = false) => {
    if (isLoadingPublishers || !isPublisherVisible) {
      return;
    }
    setIsErrorPublishers(false);
    if (!isLastPagePublishers || refresh) {
      if (refresh) {
        setPublishersData([]);
      }
      setIsLoadingPublishers(true);
      const result = await getPublishersByLetter({
        tabId,
        page: refresh ? 1 : publishersPage,
        pageSize: 20,
        ...filtersResult,
        // keywords: selectedPublisherFilter?.publisher || '',
        ...selectedPublisherFilter,
      });

      if (!result) {
        setIsErrorPublishers(true);
        setIsLoadingPublishers(false);
        setIsLastPagePublishers(true);
        return;
      }

      setPublishersData(prev => {
        if (refresh) {
          return result?.data || [];
        } else {
          return [...prev, ...(result?.data || [])].filter(
            (value: any, index, self) => {
              return (
                self.findIndex((obj: any) =>
                  ['id'].every(key => obj[key] === value[key]),
                ) === index
              );
            },
          );
        }
      });

      setPublishersPage(prev => (refresh ? 2 : prev + 1));
      const totalPageCount = Math.ceil(
        parseFloat(result?.total ? result?.total + '' : '2') / pageSize,
      );
      setIsLastPagePublishers((refresh ? 1 : publishersPage) >= totalPageCount);
      setIsLoadingPublishers(false);
    }
  };

  useEffect(() => {
    loadMore(true);
  }, [filtersResult, search]);

  useEffect(() => {
    if (publisherIdx > -1) {
      loadMorePublishers(true);
    }
  }, [selectedPublisherFilter, tabId, currentUser]);

  const _renderFooter = () => (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: isLoading && data?.length > 0 ? '5%' : 0,
      }}>
      {isLoading && data?.length > 0 && <Loader color={theme.primary} />}
      <Space bottomInset={bottomInset} extraSpace={hdp(15)} />
    </View>
  );

  const _renderEmpty = () => {
    const extraSpace =
      hdp(10) + (isPublisherVisible ? publisherHeight + hdp(2) : 0);
    if (isError) {
      return (
        <EmptyContainer
          responsiveWidth={getByScreenSize('100%', '40%')}
          extraSpace={extraSpace}>
          <EmptyList
            iconName={'alert'}
            iconColor={theme.primary}
            iconSize={theme.text.s1 * 2}
            onRefreshPress={() => loadMore(true)}
            title={'app.error'}
            showRefresh
          />
        </EmptyContainer>
      );
    } else if (isLoading) {
      return (
        <SearchListSkeleton
          style={{
            width: isWeb ? '94%' : staticWdp(94),
            alignSelf: 'center',
          }}
          marginBottom={theme.text.s12}
          numberOfItems={4}
        />
      );
    } else {
      return (
        <EmptyContainer
          responsiveWidth={getByScreenSize('100%', '40%')}
          extraSpace={extraSpace}>
          <EmptyList
            iconName={'empty'}
            iconColor={theme.primary}
            iconSize={theme.text.s1 * 2}
            title={'app.empty'}
          />
        </EmptyContainer>
      );
    }
  };

  const openFilterModal = () => {
    const tempFilters = Array.isArray(filters)
      ? filters.filter(i => i.key !== 'publisher')
      : [];
    if (tempFilters === null) {
      return;
    }
    showFiltersModal({
      filters: tempFilters,
      filtersValues: filtersResult,
      onSubmit: val => setFiltersResult(val),
    });
  };

  const searchStaticRecommendations = (text: string) => {
    const term = (text || '')?.toLowerCase().trim();

    if (term.length === 0) {
      setLocalSearchResults(data);
      setNewsCount(data.length + '');
      return;
    }

    const results = data?.filter(item => {
      const wordsInTitle = (item?.title || '')?.toLowerCase().split(' ');
      return wordsInTitle.some(word => (word || '').includes(term));
    });
    setNewsCount(results?.length + '');
    setLocalSearchResults(results);
  };

  const _renderFilterItem = (item: FilterType) => {
    return (
      <FilterItemContainer
        style={{width: wdp(getByScreenSize(90, 40))}}
        key={item?.key}>
        <IconContainer>
          <Icon
            name={'x'}
            color={theme.text.grey}
            size={theme.text.s55}
            role={'button'}
            onPress={() => setIsPublisherVisible(false)}
          />
        </IconContainer>
        <FilterLabel semiBold>{'Find Publisher by Alphabet'}</FilterLabel>
        <FilterValuesContainer>
          {(item?.items || []).map(filterItem => (
            <DrugFilterItem
              key={filterItem?.id}
              name={filterItem?.value}
              data={publishersData || []}
              onPress={() => {
                setIsFilterModalShouldClose(false);
                if (item.key) {
                  setSelectedPublisherFilter({[item.key]: filterItem.value});
                }
              }}
              onConfirmSelection={(selectedItem: any) => {
                if (!selectedItem) {
                  return;
                }
                setSelectedPublisher(selectedItem);
                if (Object.values(selectedItem).length > 0) {
                  handleChange(
                    item.key || '',
                    //@ts-ignore
                    Object.values(selectedItem || {})?.[0]?.id,
                    setFiltersResult,
                  );
                }
                if (Object.keys(selectedItem || {}).length > 0) {
                  setSelectedPublisherAlphabet(
                    Object.keys(selectedItem || {})[0],
                  );
                }
              }}
              selectedDrug={selectedPublisher}
              isLoading={isLoadingPublishers}
              isError={isErrorPublishers}
              onRefreshPress={() => loadMorePublishers(true)}
              onEndReached={() => loadMorePublishers()}
              isFilterModalShouldClose={isFilterModalShouldClose}
            />
          ))}
        </FilterValuesContainer>
      </FilterItemContainer>
    );
  };

  const renderHeader = () => {
    return (
      <HeaderSearch
        filters={filters || []}
        filtersResult={filtersResult || {}}
        onFilterPress={openFilterModal}
        setFiltersResult={setFiltersResult}
        scrollY={scrollY}
        onChangeText={text => {
          if (staticRecommendation) {
            searchStaticRecommendations(text);
          }
          setSearch(text);
          setIsFilterModalShouldClose(true);
          setIsPublisherVisible(false);
        }}
        value={search}
        newsCount={
          staticRecommendation
            ? search.length > 0
              ? newsCount || '0'
              : undefined
            : newsCount || '0'
        }
        isLoading={isLoading && data.length === 0}
        isSearchHidden={isRecommendation || isRecommendationByTab}
        isFiltersHidden={
          isRecommendation ||
          isHomeRecommendation ||
          staticRecommendation ||
          (Array.isArray(filters)
            ? filters.filter(i => i.key !== 'publisher').length === 0
            : true)
        }
        publisherActiveFilter={selectedPublisherAlphabet}
        publisherActiveName={
          selectedPublisher?.[selectedPublisherAlphabet]?.name || ''
        }
        onPublisherClear={() => {
          handleChange('publisher', '', setFiltersResult);
          setIsPublisherVisible(false);
          setSelectedPublisherAlphabet('');
          setSelectedPublisher({});
        }}
        showPublisherIcon={publisherIdx > -1}
        onPublisherPress={() => setIsPublisherVisible(prev => !prev)}
        renderAdditionalFilters={() => {
          return (
            <FiltersItemsContainer
              pointerEvents={isPublisherVisible ? 'auto' : 'none'}
              style={{
                display: isPublisherVisible ? 'flex' : 'none',
                pointerEvents: isPublisherVisible ? 'auto' : 'none',
                zIndex: 100,
              }}
              responsiveWidth={wdp(getByScreenSize(94, 20))}
              onLayout={(event: LayoutChangeEvent) => {
                const {height} = event.nativeEvent.layout;
                setPublisherHeight(height);
              }}
              {...(Platform.OS === 'web'
                ? {className: 'screen-fade-in-up'}
                : {entering: FadeInUp, exiting: FadeOutUp})}>
              {publisherIdx > -1 && _renderFilterItem(filters?.[publisherIdx])}
            </FiltersItemsContainer>
          );
        }}
      />
    );
  };

  useLayoutEffect(() => {
    navigation?.setOptions({
      header: renderHeader,
      headerShown: true,
    });
  }, [
    filtersResult,
    filters,
    search,
    newsCount,
    isLoading,
    isRecommendation,
    isRecommendationByTab,
    isHomeRecommendation,
    data,
    publishersData,
    publishersPage,
    selectedPublisher,
    isLoadingPublishers,
    isErrorPublishers,
    isPublisherVisible,
    selectedPublisherFilter,
    selectedPublisherAlphabet,
    isLastPagePublishers,
    isFilterModalShouldClose,
  ]);

  const onItemPress = (item: NewsItemType) => {
    if (staticRecommendation) {
      if (item?.pdf_link) {
        navigation?.navigate(screens.content.fileViewer, {
          ...item,
        });
      }
      return;
    }
    navigation?.navigate(screens.content.latestArticle, {
      item,
      isRecommendation: isRecommendation,
      isRecommendationByTab: isRecommendationByTab,
    });
  };

  const _renderItems = (item: NewsItemType, index: number) => {
    return (
      <Animated.View
        key={item.id}
        entering={item?.img && item?.title ? FadeInLeft : FadeInRight}>
        <LatestNewsItem
          index={index}
          title={item?.title || item?.name || ''}
          specialty={item?.subspeciality || item?.specialty || ''}
          image={{uri: item?.img || ''}}
          style={{
            opacity: !!staticRecommendation && !item?.pdf_link ? 0.9 : 1,
          }}
          isSmall={!!item?.img || !!staticRecommendation}
          onPress={() => onItemPress(item)}
        />
      </Animated.View>
    );
  };

  const onOptionTabPress = (type: NewsOptionTab) => {
    navigation?.navigate(screens.content.summaries, {
      tabId,
      // tabFilters,
      filters: filtersResult,
      summaryDateInfo,
      isSummary: type,
      type,
    });
  };

  const renderScrollView = () => {
    const displayData =
      staticRecommendation && (search || '').trim().length > 0
        ? localSearchResults
        : data;

    const contentContainerStyle = {
      flexGrow: 1,
      paddingHorizontal: wdp(getByScreenSize(3, 15)),
      paddingTop:
        topInset +
        (filtersNum > 0
          ? theme.text.s55 + headerHeight * 0.6
          : theme.text.s55) +
        (isPublisherVisible ? publisherHeight + theme.text.s55 : 0),
      minHeight: hdp(100),
    };

    return (
      <AnimatedScrollView
        style={{
          height: isWeb ? hdp(100) : 'auto',
        }}
        contentContainerStyle={contentContainerStyle}
        onScroll={isWeb ? webScrollHandler : animatedScrollHandler}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={true}
        refreshControl={
          <RefreshControl
            tintColor={theme.primary}
            refreshing={isLoading && data?.length === 0}
            onRefresh={() => loadMore(true)}
            progressViewOffset={topInset / 2}
          />
        }>
        <View
          style={{
            marginBottom: theme.text.s55,
            width: isWeb ? '94%' : staticWdp(94),
            alignSelf: 'center',
          }}>
          <SelectSummaries
            isVisible={
              !!(
                tabId &&
                !isPublisherVisible &&
                summaryDateInfo &&
                ((summaryDateInfo?.lastSummaryDates &&
                  summaryDateInfo.lastSummaryDates.length > 0))
              )
            }
            onOptionTabPress={onOptionTabPress}
          />
        </View>
        {displayData.length > 0
          ? displayData.map(_renderItems)
          : _renderEmpty()}
        {_renderFooter()}
      </AnimatedScrollView>
    );
  };

  return (
    <MainLayout
      showOverlay={false}
      isCustomHeader
      enableScroll={false}
      noPadding
      noPaddingHeader>
      {renderScrollView()}
    </MainLayout>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    auth: state.auth,
  };
};
export default connect(mapStateToProps, null)(ForYou);
